import { Box, CircularProgress, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { authColors } from '../../config/branding';
import { en } from '../../locale';

interface StickySaveBarProps {
  changeCount: number;
  onSave: () => void;
  isSaving?: boolean;
}

function formatChangeLabel(count: number): string {
  return en.common.changeCount(count);
}

export default function StickySaveBar({
  changeCount,
  onSave,
  isSaving = false,
}: StickySaveBarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const leftBg = alpha(isDark ? authColors.background : theme.palette.text.primary, 0.3);
  const rightBg = alpha(theme.palette.primary.main, 0.3);
  const rightHoverBg = alpha(theme.palette.primary.main, 0.42);
  const borderColor = alpha(theme.palette.divider, isDark ? 1 : 0.9);

  if (changeCount <= 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.snackbar,
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        pb: 2,
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'stretch',
          maxWidth: '100%',
          borderRadius: 0.5,
          overflow: 'hidden',
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.75,
            py: 1,
            bgcolor: leftBg,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}
          >
            {formatChangeLabel(changeCount)}
          </Typography>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={onSave}
          disabled={isSaving}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            border: 'none',
            cursor: isSaving ? 'default' : 'pointer',
            font: 'inherit',
            fontSize: '0.8125rem',
            fontWeight: 600,
            lineHeight: 1.2,
            color: theme.palette.primary.contrastText,
            whiteSpace: 'nowrap',
            bgcolor: rightBg,
            transition: 'background-color 0.15s ease',
            '&:hover:not(:disabled)': {
              bgcolor: rightHoverBg,
            },
            '&:disabled': {
              opacity: 0.85,
            },
          }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={14} color="inherit" />
              {en.common.saving}
            </>
          ) : (
            en.common.save
          )}
        </Box>
      </Box>
    </Box>
  );
}
